///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { SweepContext } from "@/earthbrowser/libs/poly2tri"
import { logger } from "@/utils/LogDispatcher"
import { type cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { type sphere } from "@stovokor/math/sphere"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Silhouette of the classic newscast tropical cyclone symbol, built inside a
// unit-radius disc: an open-eyed core disc with two tapering comma-shaped
// arms 180° apart. All dimensions are fractions of the outer (tip) radius,
// so callers size the symbol purely with a node scale transform.
const cEyeRadius = 0.16
const cCoreRadius = 0.46
const cTipRadius = 1.0
/** Angular width of an arm's attachment arc on the core disc */
const cArmBaseSpan = 70 * trig.rad
/** Total angle an arm sweeps from its outer base to its pointed tip */
const cArmSweep = 170 * trig.rad
/** Outer edge growth exponent: < 1 bulges the arm early for the comma look */
const cOuterShape = 0.62
/** Inner edge growth exponent: > 1 hugs the core before rising to the tip */
const cInnerShape = 1.3

const cOuterSamples = 24
const cInnerSamples = 20
const cGapSamples = 12
const cEyeSamples = 36

/** Half the extruded thickness, as a fraction of the tip radius */
export const symbolHalfThickness = 0.12

/**
 * Extruded 3D mesh of the tropical cyclone symbol in the XY plane (thickness
 * along Z), matching the vertex layout of TextGeometry so it renders through
 * the same lit pipeline as the synoptic H/L pressure markers.
 *
 * Chirality: the arms trail the rotation so the symbol reads as being wound
 * by its own spin. Pass counterclockwise=true for northern hemisphere storms.
 */
export class HurricaneSymbolGeometry implements Geometry {
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	public mode: DrawMode = DrawMode.Triangles
	public position: VertexAttribute
	public normal: VertexAttribute
	public texcoord: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer

	private index = 0

	constructor(context: StovokorContext, counterclockwise: boolean) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.texcoord = new VertexAttribute(context, 2)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.texcoord])

		// A CCW-spinning symbol needs arms that extend clockwise (trailing)
		const mirror = counterclockwise ? -1 : 1
		const outline = ensureWinding(symbolOutline(mirror), true)
		const eye = ensureWinding(circlePoints(cEyeRadius, cEyeSamples), false)

		try {
			// Copies passed in because poly2tri reorders the arrays
			const ctx = new SweepContext([...outline])
			ctx.addHole([...eye])
			ctx.triangulate()

			for (const triangle of ctx.getTriangles()) {
				const points = triangle.getPoints()
				this.addFace(points[0], points[1], points[2], symbolHalfThickness, 1)
				this.addFace(points[0], points[1], points[2], -symbolHalfThickness, -1)
			}
		} catch (error) {
			logger.warn("HurricaneSymbolGeometry: triangulation failed", error)
		}

		this.addSidePanels(outline)
		this.addSidePanels(eye)

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	/** One face triangle at height z, wound so its right-hand-rule normal
	 *  matches normalZ (backface culling is enabled globally) */
	private addFace(a: point, b: point, c: point, z: number, normalZ: number): void {
		for (const p of [a, b, c]) {
			this.position.addPos(p.x, p.y, z)
			this.normal.add(0, 0, normalZ)
			this.texcoord.add((p.x + 1) * 0.5, (p.y + 1) * 0.5)
		}
		const ccw = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x) > 0
		if (ccw === normalZ > 0) {
			this.indexBuffer.triangle(this.index, this.index + 1, this.index + 2)
		} else {
			this.indexBuffer.triangle(this.index, this.index + 2, this.index + 1)
		}
		this.index += 3
	}

	/**
	 * Extrusion walls along a closed loop. With a CCW outer loop the normal
	 * (dy, -dx) points away from the solid; with a CW hole loop the same
	 * formula points into the hole, which is also away from the solid.
	 */
	private addSidePanels(loop: vec2[]): void {
		let edgeDistance = 0
		for (let i = 0; i < loop.length; i++) {
			const v1 = loop[i]
			const v2 = loop[(i + 1) % loop.length]
			const dx = v2.x - v1.x
			const dy = v2.y - v1.y
			const length = Math.sqrt(dx * dx + dy * dy)
			if (length < 1e-8) {
				continue
			}
			const nx = dy / length
			const ny = -dx / length

			const u1 = edgeDistance
			const u2 = edgeDistance + length
			edgeDistance = u2

			// Quad layout: idx+0=v1 bottom, idx+1=v2 bottom, idx+2=v1 top, idx+3=v2 top
			this.position.addPos(v1.x, v1.y, -symbolHalfThickness)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(u1, 0)
			this.position.addPos(v2.x, v2.y, -symbolHalfThickness)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(u2, 0)
			this.position.addPos(v1.x, v1.y, symbolHalfThickness)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(u1, 1)
			this.position.addPos(v2.x, v2.y, symbolHalfThickness)
			this.normal.add(nx, ny, 0)
			this.texcoord.add(u2, 1)

			// Wound so the right-hand-rule normal points along (nx, ny)
			this.indexBuffer.triangle(this.index, this.index + 3, this.index + 2)
			this.indexBuffer.triangle(this.index, this.index + 1, this.index + 3)
			this.index += 4
		}
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

interface point {
	x: number
	y: number
}

function polar(angle: number, radius: number): vec2 {
	return new vec2(Math.cos(angle) * radius, Math.sin(angle) * radius)
}

/**
 * The union of the core disc and both arms as one simple polygon. Each arm's
 * outer edge runs base→tip, its inner edge tip→base, then the core circle
 * arc bridges to the next arm, so nothing overlaps or z-fights.
 */
function symbolOutline(mirror: number): vec2[] {
	const points: vec2[] = []
	const radiusRange = cTipRadius - cCoreRadius

	for (let arm = 0; arm < 2; arm++) {
		const base = arm * Math.PI

		// Outer edge: bulges out quickly, ends at the pointed tip
		for (let i = 0; i <= cOuterSamples; i++) {
			const t = i / cOuterSamples
			const angle = base + t * cArmSweep
			const radius = cCoreRadius + radiusRange * Math.pow(t, cOuterShape)
			points.push(polar(mirror * angle, radius))
		}

		// Inner edge: from just below the tip back down to the core circle
		for (let i = cInnerSamples - 1; i >= 0; i--) {
			const t = i / cInnerSamples
			const angle = base + cArmBaseSpan + t * (cArmSweep - cArmBaseSpan)
			const radius = cCoreRadius + radiusRange * Math.pow(t, cInnerShape)
			points.push(polar(mirror * angle, radius))
		}

		// Core circle arc bridging to the opposite arm's outer base
		for (let i = 1; i < cGapSamples; i++) {
			const angle = base + cArmBaseSpan + (i / cGapSamples) * (Math.PI - cArmBaseSpan)
			points.push(polar(mirror * angle, cCoreRadius))
		}
	}
	return points
}

function circlePoints(radius: number, samples: number): vec2[] {
	const points: vec2[] = []
	for (let i = 0; i < samples; i++) {
		points.push(polar((i / samples) * Math.PI * 2, radius))
	}
	return points
}

/** Force a loop's winding (shoelace sign) so panel normals stay predictable */
function ensureWinding(points: vec2[], counterclockwise: boolean): vec2[] {
	let area = 0
	for (let i = 0; i < points.length; i++) {
		const a = points[i]
		const b = points[(i + 1) % points.length]
		area += a.x * b.y - b.x * a.y
	}
	return (area > 0) === counterclockwise ? points : points.reverse()
}
